import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AlertTriangle, BarChart3, Download, Factory, Loader2, Minus, Package, Plus, Printer } from "lucide-react";
import { toast } from "sonner";
import ShelfView from "@/components/ShelfView";
import RollStockDialog from "@/components/RollStockDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Link } from "react-router-dom";
import { GroupedVariantChart } from "@/components/inventory/InventoryCharts";
import { useCoverage } from "@/components/inventory/useCoverage";
import { printProductionReport } from "@/lib/productionReportPrint";
import { moNum } from "@/lib/moNum";
import * as XLSX from "xlsx";
import { StockPopover } from "@/components/inventory/StockPopover";
import { ReservationsDialog } from "@/components/inventory/ReservationsDialog";
import { ConsumptionDialog } from "@/components/inventory/ConsumptionDialog";
import { OnOrderDialog } from "@/components/inventory/OnOrderDialog";
import { printInventoryReport } from "@/components/inventory/inventoryPrint";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const isLabelComp = (name) => /label/i.test(name || "");
const isAlumComp = (name) => /alum/i.test(name || "");

// Water is sourced from the spring — not tracked as inventory (still shown on Product BOMs).
const isExcludedComp = (name) => /water/i.test(name || "");

const PALETTE = ["#002FA7", "#0EA5E9", "#059669", "#D97706", "#DC2626", "#7C3AED", "#DB2777", "#475569"];

const num = (n) => Math.round(n).toLocaleString();

const GRID = "gap-x-3 grid-cols-[minmax(140px,1.4fr)_minmax(52px,0.55fr)_minmax(150px,1.5fr)_minmax(60px,0.65fr)_minmax(72px,0.85fr)_minmax(84px,1fr)_minmax(76px,0.9fr)_minmax(84px,1fr)_minmax(84px,1fr)_minmax(72px,0.85fr)]";

function CpAdjust({ onApply, testId }) {
  const [amt, setAmt] = useState("");
  const [busy, setBusy] = useState(false);
  const apply = async (sign) => {
    const n = parseFloat(amt);
    if (!n || n <= 0) { toast.error("Enter a quantity first"); return; }
    setBusy(true);
    try { await onApply(sign * n); setAmt(""); } finally { setBusy(false); }
  };
  return (
    <span className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      <Input type="number" min="0" value={amt} onChange={(e) => setAmt(e.target.value)} placeholder="Qty" className="h-6 w-16 rounded-sm px-1.5 text-xs tabular-nums" data-testid={`${testId}-amt`} />
      <button type="button" disabled={busy} onClick={() => apply(-1)} title="Subtract" className="flex h-6 w-6 items-center justify-center rounded-sm border border-slate-200 text-slate-500 hover:border-red-300 hover:text-red-600" data-testid={`${testId}-minus`}><Minus className="h-3 w-3" /></button>
      <button type="button" disabled={busy} onClick={() => apply(1)} title="Add" className="flex h-6 w-6 items-center justify-center rounded-sm border border-slate-200 text-slate-500 hover:border-emerald-300 hover:text-emerald-600" data-testid={`${testId}-plus`}><Plus className="h-3 w-3" /></button>
    </span>
  );
}

function CoPackerCell({ rows, testId, covFor, coPackers = [], onAdjust }) {
  const total = rows.reduce((a, r) => a + (r.qty || 0), 0);
  const [addCp, setAddCp] = useState("");
  const empty = !rows.length || Math.abs(total) < 0.01;
  if (empty && !onAdjust) return <span className="text-right text-sm text-slate-300">—</span>;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className={`text-right text-sm font-semibold tabular-nums underline decoration-dotted underline-offset-2 ${empty ? "text-slate-300" : total < 0 ? "text-red-600" : "text-violet-700"}`} data-testid={testId}>
          {empty ? "—" : Math.round(total).toLocaleString()}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 rounded-sm p-3" onClick={(e) => e.stopPropagation()}>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">At Co-Packers</p>
        {rows.map((r, i) => {
          const cov = covFor && r.qty > 0 ? covFor(r.qty) : null;
          return (
            <div key={r.cpId || r.name || i} className="border-b border-slate-100 py-1.5 last:border-b-0" data-testid={`copacker-pop-row-${i}`}>
              <div className="flex items-center justify-between gap-3 text-sm">
                <span className="truncate font-semibold text-slate-700">{r.name}</span>
                <span className={`whitespace-nowrap tabular-nums font-semibold ${r.qty < 0 ? "text-red-600" : "text-slate-800"}`}>
                  {Math.round(r.qty).toLocaleString()} <span className="text-[10px] font-normal text-slate-400">units</span>
                </span>
              </div>
              {onAdjust && r.cpId && <div className="mt-1 flex justify-end"><CpAdjust testId={`${testId}-adj-${i}`} onApply={(d) => onAdjust(r.cpId, d)} /></div>}
              {cov && cov.covRows.map((cr, j) => (
                <div key={cr.label || j} className="mt-1 flex items-start justify-between gap-3 text-[11px] tabular-nums">
                  <span className="truncate pt-0.5 text-slate-500">{cr.label}</span>
                  <span className="whitespace-nowrap text-right leading-snug text-slate-500">
                    <span className="block"><span className="font-semibold text-slate-700">{cr.cov.toLocaleString()}</span> trucks</span>
                    {cr.ppt ? <span className="block"><span className="font-semibold text-slate-700">{Math.floor(cr.cov * cr.ppt).toLocaleString()}</span> pallets</span> : null}
                    {cr.cpt ? <span className="block"><span className="font-semibold text-slate-700">{Math.floor(cr.cov * cr.cpt).toLocaleString()}</span> cases</span> : null}
                  </span>
                </div>
              ))}
            </div>
          );
        })}
        {onAdjust && coPackers.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-1 border-t border-slate-100 pt-2">
            <Select value={addCp || undefined} onValueChange={setAddCp}>
              <SelectTrigger className="h-6 w-[120px] rounded-sm px-1.5 text-xs" data-testid={`${testId}-add-cp`}><SelectValue placeholder="Co-packer…" /></SelectTrigger>
              <SelectContent>{coPackers.map((c) => (<SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>))}</SelectContent>
            </Select>
            <CpAdjust testId={`${testId}-add`} onApply={async (d) => { if (!addCp) { toast.error("Pick a co-packer first"); return; } await onAdjust(addCp, d); }} />
          </div>
        )}
        <Link to="/copacker-inventory" className="mt-2 inline-block text-xs font-semibold text-[#002FA7] hover:underline">Manage on Co-Packer Inventory →</Link>
      </PopoverContent>
    </Popover>
  );
}

// Click-to-open truckload coverage for a stock quantity. getData computed lazily on open.
function TruckloadPopover({ getData, testId, children, note = "Available stock (after reservations) ÷ units required for one full truckload on each Product BOM." }) {
  const [open, setOpen] = useState(false);
  const d = open ? getData() : null;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="rounded-sm text-right leading-tight transition-colors hover:bg-slate-100" title="Click to see truckloads supported by this stock" data-testid={testId}>
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 rounded-sm p-3">
        {d && (d.covRows.length === 0 ? (
          <p className="text-xs text-slate-500">No Product BOM with a full truckload configuration (case pack + cases/pallet + pallets/truck) uses this component.</p>
        ) : (
          <>
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Truckloads Supported</p>
            {d.covRows.map((r, i) => (
              <div key={r.label || i} className="border-b border-slate-100 py-1.5 last:border-b-0" data-testid={`truckload-row-${i}`}>
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="truncate text-slate-600">{r.label}</span>
                  <span className="whitespace-nowrap tabular-nums font-semibold text-slate-800">{r.cov.toLocaleString()} <span className="text-[10px] font-normal text-slate-400">truck{r.cov === 1 ? "" : "s"}</span></span>
                </div>
                <div className="flex items-center justify-between gap-3 text-[11px] tabular-nums">
                  <span className="text-slate-400">{Math.round(r.req).toLocaleString()} per truckload</span>
                  {r.ppt ? <span className="whitespace-nowrap text-slate-500"><span className="font-semibold text-slate-700">{Math.floor(r.cov * r.ppt).toLocaleString()}</span> pallets</span> : null}
                </div>
                {r.cpt ? (
                  <div className="flex justify-end text-[11px] tabular-nums">
                    <span className="whitespace-nowrap text-slate-500"><span className="font-semibold text-slate-700">{Math.floor(r.cov * r.cpt).toLocaleString()}</span> cases</span>
                  </div>
                ) : null}
              </div>
            ))}
            {d.covAvg && <p className="mt-2 text-right text-xs font-bold text-[#002FA7]" data-testid="truckload-avg">Average: {d.cov.toLocaleString()} truckloads</p>}
            <p className="mt-2 text-[10px] text-slate-400">{note}</p>
          </>
        ))}
      </PopoverContent>
    </Popover>
  );
}

// Read-only On Order cell — clickable when backed by open supplier orders.
function OnOrderCell({ rkey, onOrder, onOpen }) {
  const val = onOrder[rkey]?.on_order || 0;
  return (
    <div className="pr-10 text-right">
      {val > 0 ? (
        <button type="button" onClick={() => onOpen(rkey)} className="tabular-nums text-sm font-medium text-[#002FA7] hover:underline" data-testid={`inventory-onorder-${rkey}`}>
          {num(val)}
        </button>
      ) : (
        <span className="tabular-nums text-sm text-slate-300" data-testid={`inventory-onorder-${rkey}`}>0</span>
      )}
    </div>
  );
}

// Read-only Consumed (last 30 days) cell — clickable when there is any consumption history.
function ConsumedCell({ rkey, consumption, onOpen }) {
  const e = consumption[rkey];
  const val = e?.consumed_30d || 0;
  return (
    <div className="text-right">
      {(e?.orders || []).length > 0 ? (
        <button type="button" onClick={() => onOpen(rkey)} title={`All-time consumed: ${num(e.consumed || 0)} — click for full history`}
          className="tabular-nums text-sm font-medium text-emerald-600 hover:underline" data-testid={`inventory-consumed-${rkey}`}>
          {num(val)}
        </button>
      ) : (
        <span className="tabular-nums text-sm text-slate-300" data-testid={`inventory-consumed-${rkey}`}>0</span>
      )}
    </div>
  );
}

// Split a reservations entry into sales-MO vs projected-MO totals.
function splitReserved(entry) {
  const total = entry?.reserved || 0;
  const proj = (entry?.orders || []).filter((o) => o.projected).reduce((s, o) => s + (o.reserved || 0), 0);
  return { total, proj: Math.round(proj * 100) / 100, sales: Math.round((total - proj) * 100) / 100 };
}

// Read-only Reserved (Sales / Projections) + Available cascade cells shared by both row types.
function ReservedAvailCells({ rkey, stock, reservations, onOrder, onOpen, covFor }) {
  const { total: reserved, sales, proj } = splitReserved(reservations[rkey]);
  const availSales = stock - sales;
  const available = stock - reserved;
  const projected = stock + (onOrder[rkey]?.on_order || 0) - reserved;
  const resvCell = (val, testId, mode) => (
    <div className="text-right">
      {val > 0 ? (
        <button type="button" onClick={() => onOpen(mode)} className="tabular-nums text-sm font-medium text-[#002FA7] hover:underline" data-testid={testId}>
          {num(val)}
        </button>
      ) : (
        <span className="tabular-nums text-sm text-slate-400" data-testid={testId}>0</span>
      )}
    </div>
  );
  return (
    <>
      {resvCell(sales, `inventory-reserved-${rkey}`, "sales")}
      {resvCell(proj, `inventory-reserved-proj-${rkey}`, "projections")}
      <div className="flex items-center justify-end gap-1">
        {availSales < 0 && <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-red-600" data-testid={`inventory-avail-sales-warning-${rkey}`} />}
        <TruckloadPopover getData={() => covFor(availSales)} testId={`inventory-avail-sales-trucks-${rkey}`}
          note="Available after sales (stock − reserved for sales) ÷ units required for one full truckload on each Product BOM.">
          <span className={`tabular-nums text-sm underline decoration-dotted decoration-slate-300 underline-offset-2 ${availSales < 0 ? "font-semibold text-red-600" : "text-slate-800"}`} data-testid={`inventory-avail-sales-${rkey}`}>
            {num(availSales)}
          </span>
        </TruckloadPopover>
      </div>
      <div className="flex items-center justify-end gap-1">
        {available < 0 && <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-red-600" data-testid={`inventory-available-warning-${rkey}`} />}
        <TruckloadPopover getData={() => covFor(available)} testId={`inventory-avail-trucks-${rkey}`}
          note="Available after projections (stock − reserved for sales − reserved for projections) ÷ units required for one full truckload on each Product BOM.">
          <span className={`tabular-nums text-sm underline decoration-dotted decoration-slate-300 underline-offset-2 ${available < 0 ? "font-semibold text-red-600" : "text-slate-800"}`} data-testid={`inventory-available-${rkey}`}>
            {num(available)}
          </span>
        </TruckloadPopover>
      </div>
      <div className="flex items-center justify-end gap-1">
        {projected < 0 && <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-red-600" data-testid={`inventory-projected-warning-${rkey}`} />}
        <TruckloadPopover getData={() => covFor(projected)} testId={`inventory-proj-trucks-${rkey}`}
          note="Available after purchases (stock + on order − all reservations) ÷ units required for one full truckload on each Product BOM.">
          <span className={`tabular-nums text-sm underline decoration-dotted decoration-slate-300 underline-offset-2 ${projected < 0 ? "font-semibold text-red-600" : "text-slate-600"}`} data-testid={`inventory-projected-${rkey}`}>
            {num(projected)}
          </span>
        </TruckloadPopover>
      </div>
    </>
  );
}

export default function ComponentInventory() {
  const [components, setComponents] = useState([]);
  const [qtys, setQtys] = useState({});
  const [reservations, setReservations] = useState({});
  const [onOrder, setOnOrder] = useState({});
  const [resvKey, setResvKey] = useState(null);
  const [resvName, setResvName] = useState("");
  const [resvMode, setResvMode] = useState("sales");
  const [consumption, setConsumption] = useState({});
  const [consKey, setConsKey] = useState(null);
  const [consName, setConsName] = useState("");
  const [ooKey, setOoKey] = useState(null);
  const [ooName, setOoName] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [mode, setMode] = useState("graph");
  const [showCharts, setShowCharts] = useState(() => localStorage.getItem("ci_show_charts") === "1");
  const toggleCharts = () => setShowCharts((v) => { localStorage.setItem("ci_show_charts", v ? "0" : "1"); return !v; });

  const REPORT_COMPS = /preform|cap|label|tray/i;
  const variantRows = (c, t) => {
    const dlist = (t.designs || []).length ? t.designs : [{ id: "", name: "" }];
    return dlist.map((d) => ({
      key: d.id ? `${t.id}::${d.id}` : t.id,
      design: d.name || "",
      name: [c.name, t.name, d.name].filter(Boolean).join(" · "),
    }));
  };
  const printProdReport = () => {
    const secs = [];
    for (const c of invComponents) {
      if (!REPORT_COMPS.test(c.name || "")) continue;
      for (const t of c.types || []) {
        for (const r of variantRows(c, t)) {
          const stock = Math.max(0, parseFloat(qtys[r.key]) || 0);
          const resv = reservations[r.key] || {};
          const oo = onOrder[r.key]?.on_order || 0;
          if (!stock && !(resv.reserved > 0) && !oo) continue;
          const { sales, proj } = splitReserved(resv);
          secs.push({
            title: r.name, stock, onOrder: oo, reserved: resv.reserved || 0,
            reservedSales: sales, reservedProj: proj,
            orders: (resv.orders || []).map((o) => ({ ...o, mo: moNum(o.mwo_number) || "—" })),
          });
        }
      }
    }
    if (!secs.length) { toast.error("Nothing to report — no Preforms/Caps/Labels/Trays stock or reservations found"); return; }
    printProductionReport(secs);
  };
  const exportIvpExcel = async () => {
    const secs = [];
    for (const c of invComponents) {
      if (!REPORT_COMPS.test(c.name || "")) continue;
      for (const t of c.types || []) {
        for (const r of variantRows(c, t)) {
          const stock = Math.max(0, parseFloat(qtys[r.key]) || 0);
          const resv = reservations[r.key] || {};
          const oo = onOrder[r.key]?.on_order || 0;
          if (!stock && !(resv.reserved > 0) && !oo) continue;
          const { sales, proj } = splitReserved(resv);
          secs.push({
            title: r.name, stock, onOrder: oo, reserved: resv.reserved || 0,
            reservedSales: sales, reservedProj: proj,
            orders: (resv.orders || []).map((o) => ({ ...o, mo: moNum(o.mwo_number) || "—" })),
          });
        }
      }
    }
    if (!secs.length) { toast.error("Nothing to export — no Preforms/Caps/Labels/Trays stock or reservations found"); return; }
    let poMap = {};
    try {
      const [mosR, sosR] = await Promise.all([api.get("/manufacturing-orders"), api.get("/sales-orders")]);
      const soById = Object.fromEntries((sosR.data || []).map((s) => [s.id, s]));
      (mosR.data || []).forEach((m) => {
        const so = soById[m.source_so_id];
        if (so) poMap[m.id] = { po: so.customer_po || "", rdd: so.ship_date || "" };
      });
    } catch { /* export continues without PO/RDD enrichment */ }
    const aoa = [];
    for (const s of secs) {
      aoa.push([s.title]);
      aoa.push(["On Order", "Units in Stock", "Reserved (Sales)", "Reserved (Projections)", "Available After Sales", "Available After Projections", "Available After Purchases"]);
      aoa.push([s.onOrder, s.stock, s.reservedSales, s.reservedProj, s.stock - s.reservedSales, s.stock - s.reserved, s.stock + s.onOrder - s.reserved]);
      if (s.orders.length) {
        aoa.push(["MO #", "Type", "Production Date", "Product", "Design", "Pack", "Produced For", "Customer PO", "RDD", "Reserved Qty"]);
        for (const o of [...s.orders].sort((a, b) => (a.projected ? 1 : 0) - (b.projected ? 1 : 0))) {
          const link = (!o.projected && poMap[o.mo_id]) || {};
          aoa.push([o.mo, o.projected ? "Projected" : "Sales", o.production_date || "", o.product_name || "",
            o.design_name || "", o.case_pack ? `${Math.round(o.case_pack)}pk` : "", o.customer_name || "",
            link.po || "", link.rdd || "", o.reserved]);
        }
      }
      aoa.push([]);
    }
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!cols"] = [{ wch: 18 }, { wch: 13 }, { wch: 15 }, { wch: 22 }, { wch: 21 }, { wch: 22 }, { wch: 22 }, { wch: 16 }, { wch: 12 }, { wch: 13 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Inventory vs Production");
    XLSX.writeFile(wb, `inventory-vs-production-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };
  const exportExcel = () => {
    const rows = [];
    for (const c of invComponents) {
      for (const t of c.types || []) {
        for (const r of variantRows(c, t)) {
          const stock = Math.max(0, parseFloat(qtys[r.key]) || 0);
          const reserved = reservations[r.key]?.reserved || 0;
          const { sales: rSales, proj: rProj } = splitReserved(reservations[r.key]);
          const oo = onOrder[r.key]?.on_order || 0;
          const cp = cpRows(c.id, t.id, r.key.includes("::") ? r.key.split("::")[1] : "").reduce((a, x) => a + (x.qty || 0), 0);
          rows.push({
            Component: c.name, Variant: t.name, Design: r.design,
            "On Order": oo, "Units in Stock": stock, "At Co-Packers": cp,
            "Reserved (Sales)": rSales, "Reserved (Projections)": rProj,
            "Available After Sales": stock - rSales,
            "Available After Projections": stock - reserved,
            "Available After Purchases": stock + oo - reserved,
            "Consumed (All Time)": consumption[r.key]?.consumed || 0,
          });
        }
      }
    }
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [{ wch: 18 }, { wch: 24 }, { wch: 26 }, { wch: 10 }, { wch: 14 }, { wch: 13 }, { wch: 14 }, { wch: 19 }, { wch: 17 }, { wch: 22 }, { wch: 21 }, { wch: 18 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Component Inventory");
    XLSX.writeFile(wb, `component-inventory-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };
  const [products, setProducts] = useState([]);
  const [viewCalc, setViewCalc] = useState("qty");
  const [covProductId, setCovProductId] = useState("");
  const [covCfgId, setCovCfgId] = useState("");
  const [materialFilter, setMaterialFilter] = useState("all");
  const [copackerStock, setCopackerStock] = useState([]);

  const load = async () => {
    try {
      const [{ data }, resv, prods, oo, cps, cons] = await Promise.all([
        api.get("/components"),
        api.get("/components/reservations"),
        api.get("/products"),
        api.get("/purchase-orders/on-order"),
        api.get("/copacker-stock").catch(() => ({ data: [] })),
        api.get("/components/consumption").catch(() => ({ data: { by_key: {} } })),
      ]);
      setComponents(data);
      setCopackerStock(cps.data || []);
      setProducts(prods.data || []);
      setReservations(resv.data?.by_key || {});
      setOnOrder(oo.data?.by_key || {});
      setConsumption(cons.data?.by_key || {});
      const q = {};
      data.forEach((c) => (c.types || []).forEach((t) => {
        q[t.id] = String(t.stock_qty ?? 0);
        const ds = t.design_stock || {};
        (t.designs || []).forEach((d) => { q[`${t.id}::${d.id}`] = String(ds[d.id] ?? 0); });
      }));
      setQtys(q);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openResv = (key, name, mode = "sales") => { setResvKey(key); setResvName(name); setResvMode(mode); };
  const openCons = (key, name) => { setConsKey(key); setConsName(name); };
  const stockFor = (key) => Math.max(0, parseFloat(qtys[key]) || 0);

  // design_id -> list of {productName, configs:[{label, bpc, cpp}]} from Product BOM design associations
  const cpRows = useCallback((cid, tid, did) => copackerStock
    .filter((s) => s.component_id === cid && (s.type_id || "") === (tid || "") && (s.design_id || "") === (did || ""))
    .map((s) => ({ name: s.co_packer_name || "Co-Packer", qty: s.qty || 0, cpId: s.co_packer_id })), [copackerStock]);

  const [coPackers, setCoPackers] = useState([]);
  useEffect(() => { api.get("/co-packers").then((r) => setCoPackers(r.data || [])).catch(() => {}); }, []);
  const adjustCp = async (componentId, typeId, designId, cpId, delta) => {
    try {
      await api.post("/copacker-stock/adjust", { co_packer_id: cpId, component_id: componentId, type_id: typeId || "", design_id: designId || "", delta, note: "Inline adjust from Component Inventory" });
      const cps = await api.get("/copacker-stock");
      setCopackerStock(cps.data || []);
      toast.success("Co-packer stock updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Adjustment failed");
    }
  };

  const [rollDlg, setRollDlg] = useState({ open: false, component: null, type: null, designId: "" });
  const modeOf = (c) => c.inventory_mode_effective || "discrete";

  // Lazy truckload-coverage data for a row's quantity popover.
  const covDataFor = (compId, key) => () => {
    const q = Math.max(0, parseFloat(qtys[key]) || 0);
    const r = Math.max(0, reservations[key]?.reserved || 0);
    return toCoverage({ typeId: key, compId, avail: Math.max(0, Math.round((q - r) * 100) / 100) });
  };

  // Stock-entry column: roll-based Add Stock for Film/Label, else the existing unit/pack controls.
  const stockCol = (c, t, key, designId = "") => {
    const invMode = modeOf(c);
    if (invMode === "film" || invMode === "label") {
      const summ = (t.roll_summary || {})[designId || "_"] || {};
      const qty = Math.max(0, parseFloat(qtys[key]) || 0);
      const unit = invMode === "label" ? "labels" : (summ.unit || t.roll_weight_unit || "lbs");
      return (
        <div className="flex items-center justify-end gap-2" data-testid={`inventory-roll-cell-${key}`}>
          <TruckloadPopover getData={covDataFor(c.id, key)} testId={`inventory-truckloads-${key}`}>
            <div className="tabular-nums text-sm font-semibold text-slate-800 underline decoration-dotted decoration-slate-300 underline-offset-2" data-testid={`inventory-derived-${key}`}>{num(qty)} <span className="text-[10px] font-normal text-slate-400">{unit}</span></div>
            <div className="text-[10px] text-slate-400">{summ.physical_rolls || 0} roll{(summ.physical_rolls || 0) === 1 ? "" : "s"}{summ.partial_count ? ` · ${summ.partial_count} partial` : ""}</div>
          </TruckloadPopover>
          <button type="button" className="rounded-sm border border-slate-200 p-1 text-[#002FA7] hover:bg-slate-100" title="Add roll stock" data-testid={`inventory-roll-add-${key}`} onClick={() => setRollDlg({ open: true, component: c, type: t, designId, view: "add" })}><Plus className="h-3.5 w-3.5" /></button>
          <button type="button" className="rounded-sm border border-slate-200 p-1 text-slate-500 hover:bg-slate-100 hover:text-red-600" title="Remove roll stock" data-testid={`inventory-roll-remove-${key}`} onClick={() => setRollDlg({ open: true, component: c, type: t, designId, view: "remove" })}><Minus className="h-3.5 w-3.5" /></button>
        </div>
      );
    }
    const isD = !!designId;
    const applyDelta = (delta) => {
      const nv = Math.max(0, (parseFloat(qtys[key]) || 0) + delta);
      setQtys((prev) => ({ ...prev, [key]: String(nv) }));
      if (isD) saveDesign(c.id, t.id, designId, nv); else save(c.id, t.id, nv);
    };
    const upcN = Number(t.units_per_case) || 0;
    const uppN = Number(t.units_per_pallet) || (upcN && Number(t.cases_per_pallet) ? upcN * Number(t.cases_per_pallet) : 0);
    const qtyN = parseFloat(qtys[key]) || 0;
    let pkParts = [];
    if (qtyN > 0 && (uppN || upcN) && t.packaging_mode !== "weight" && c.show_pallet_breakdown !== false) {
      const pallets = uppN ? Math.floor(qtyN / uppN) : 0;
      const afterP = qtyN - pallets * uppN;
      const cases = upcN ? Math.floor(afterP / upcN) : 0;
      const units = Math.round((afterP - cases * upcN) * 100) / 100;
      if (pallets > 0) pkParts.push(`${pallets.toLocaleString()} Pallet${pallets === 1 ? "" : "s"}`);
      if (cases > 0) pkParts.push(`${cases.toLocaleString()} Case${cases === 1 ? "" : "s"}`);
      if (units > 0) pkParts.push(`${units.toLocaleString()} Unit${units === 1 ? "" : "s"}`);
      pkParts = pkParts.slice(0, 2);
    }
    const isWeight = t.packaging_mode === "weight";
    const unitLabel = isWeight ? (t.weight_unit || "lbs") : (t.display_unit || "units");
    return (
      <div className="flex items-center justify-end gap-2" data-testid={`inventory-stock-cell-${key}`}>
        {savingId === key && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />}
        <TruckloadPopover getData={covDataFor(c.id, key)} testId={`inventory-truckloads-${key}`}>
          <div className="tabular-nums text-sm font-semibold text-slate-800 underline decoration-dotted decoration-slate-300 underline-offset-2" data-testid={`inventory-qty-${key}`}>
            {qtyN.toLocaleString(undefined, { maximumFractionDigits: 2 })} <span className="text-[10px] font-normal text-slate-400">{unitLabel}</span>
          </div>
          {pkParts.length > 0 ? (
            <div className="whitespace-nowrap text-[10px] tabular-nums text-slate-400" data-testid={`inventory-breakdown-${key}`}>{pkParts.join(" · ")}</div>
          ) : isWeight && (t.weight_per_unit || 0) > 0 ? (
            <div className="whitespace-nowrap text-[10px] tabular-nums text-slate-400" data-testid={`inventory-pkg-equiv-${key}`}>
              ≈ {(Math.round((qtyN / t.weight_per_unit) * 100) / 100).toLocaleString()} {(t.package_type || "package") + "s"}
            </div>
          ) : null}
        </TruckloadPopover>
        <StockPopover t={t} mode="add" testId={`inventory-add-${key}`} onApply={applyDelta} />
        <StockPopover t={t} mode="subtract" testId={`inventory-subtract-${key}`} onApply={applyDelta} />
      </div>
    );
  };

  // Components shown in inventory (Water excluded — spring-sourced, not tracked).
  const invComponents = useMemo(
    () => components.filter((c) => {
      if (isExcludedComp(c.name)) return false;
      if (materialFilter === "all") return true;
      const mats = c.materials || (c.material ? [c.material] : []);
      return mats.includes(materialFilter);
    }),
    [components, materialFilter]
  );

  const colorByComp = useMemo(
    () => Object.fromEntries(invComponents.map((c, i) => [c.id, PALETTE[i % PALETTE.length]])),
    [invComponents]
  );

  const withReserved = useCallback((key, qty) => {
    const reserved = Math.min(qty, Math.max(0, reservations[key]?.reserved || 0));
    return { qty, reserved: Math.round(reserved * 100) / 100, avail: Math.round((qty - reserved) * 100) / 100 };
  }, [reservations]);

  const buildData = useCallback((predicate) =>
    invComponents
      .filter(predicate)
      .flatMap((c) =>
        (c.types || []).flatMap((t) => {
          const ds = t.designs || [];
          if (ds.length > 0) {
            return ds.map((d) => ({
              typeId: `${t.id}::${d.id}`,
              compId: c.id,
              compName: c.name,
              name: `${t.name} · ${d.name}`,
              color: colorByComp[c.id] || "#002FA7",
              ...withReserved(`${t.id}::${d.id}`, Math.max(0, parseFloat(qtys[`${t.id}::${d.id}`]) || 0)),
            }));
          }
          return [{
            typeId: t.id,
            compId: c.id,
            compName: c.name,
            name: t.name,
            color: colorByComp[c.id] || "#002FA7",
            ...withReserved(t.id, Math.max(0, parseFloat(qtys[t.id]) || 0)),
          }];
        })
      ), [invComponents, colorByComp, qtys, withReserved]);

  const componentData = useMemo(() => buildData(() => true), [buildData]);
  const labelData = useMemo(
    () =>
      components
        .filter((c) => isLabelComp(c.name))
        .flatMap((c) =>
          (c.types || []).flatMap((t) => {
            const designs = t.designs || [];
            if (designs.length === 0) {
              return [{ typeId: t.id, compId: c.id, name: t.name, ...withReserved(t.id, Math.max(0, parseFloat(qtys[t.id]) || 0)) }];
            }
            return designs.map((d) => ({
              typeId: `${t.id}::${d.id}`,
              compId: c.id,
              name: `${t.name} · ${d.name}`,
              ...withReserved(`${t.id}::${d.id}`, Math.max(0, parseFloat(qtys[`${t.id}::${d.id}`]) || 0)),
            }));
          })
        ),
    [components, qtys, withReserved]
  );

  const groups = useMemo(
    () =>
      invComponents
        .filter((c) => (c.types || []).length)
        .map((c) => ({
          comp: { id: c.id, name: c.name, color: colorByComp[c.id] || "#002FA7" },
          variants: (c.types || []).flatMap((t) => {
            const ds = t.designs || [];
            if (ds.length > 0) {
              return ds.map((d) => ({
                typeId: `${t.id}::${d.id}`,
                name: `${t.name} · ${d.name}`,
                ...withReserved(`${t.id}::${d.id}`, Math.max(0, parseFloat(qtys[`${t.id}::${d.id}`]) || 0)),
              }));
            }
            return [{
              typeId: t.id,
              name: t.name,
              ...withReserved(t.id, Math.max(0, parseFloat(qtys[t.id]) || 0)),
            }];
          }),
        })),
    [invComponents, qtys, colorByComp, withReserved]
  );
  const yMax = useMemo(() => {
    const m = Math.max(0, ...componentData.map((d) => d.qty));
    return m <= 0 ? 10 : Math.ceil(m * 1.05);
  }, [componentData]);

  const peak = (arr) => Math.max(0, ...arr.map((d) => (d.covMode ? d.avail : d.qty)));

  const { coverage, covProduct, covCfgs, covCfg, covSpecific, displayGroups, displayLabelData, displayComponentData, displayYMax, toCoverage } =
    useCoverage({ viewCalc, products, covProductId, covCfgId, components, groups, labelData, componentData, yMax });

  // ALUM components (bottles, caps, ...) get their own chart, separate from the main one.
  const mainGroups = useMemo(() => displayGroups.filter((g) => !isAlumComp(g.comp.name)), [displayGroups]);
  const [alumSort, setAlumSort] = useState("qty"); // qty (default) | alpha
  const alumGroups = useMemo(() => {
    const val = (v) => (v.covMode ? v.avail : v.qty) || 0;
    return displayGroups
      .filter((g) => isAlumComp(g.comp.name))
      .map((g) => ({
        ...g,
        variants: [...g.variants].sort((a, b) => (alumSort === "qty" ? val(b) - val(a) : a.name.localeCompare(b.name, undefined, { numeric: true }))),
      }));
  }, [displayGroups, alumSort]);
  const groupVals = (gs) => gs.flatMap((g) => g.variants.map((v) => (v.covMode ? v.avail : v.qty)));
  const chartYMax = (gs) => {
    const m = Math.max(0, ...groupVals(gs));
    return m <= 0 ? 10 : (coverage ? m : Math.ceil(m * 1.05));
  };
  const chartPeak = (gs) => Math.max(0, ...groupVals(gs));

  const legend = useMemo(
    () => invComponents.filter((c) => (c.types || []).length),
    [invComponents]
  );

  const save = async (compId, typeId, override) => {
    const value = Math.max(0, override != null ? override : (parseFloat(qtys[typeId]) || 0));
    setSavingId(typeId);
    try {
      await api.put(`/components/${compId}/types/${typeId}/stock`, { stock_qty: value });
      setComponents((prev) =>
        prev.map((c) =>
          c.id === compId
            ? { ...c, types: c.types.map((t) => (t.id === typeId ? { ...t, stock_qty: value } : t)) }
            : c
        )
      );
      setQtys((prev) => ({ ...prev, [typeId]: String(value) }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setSavingId(null);
    }
  };

  const saveDesign = async (compId, typeId, designId, override) => {
    const key = `${typeId}::${designId}`;
    const value = Math.max(0, override != null ? override : (parseFloat(qtys[key]) || 0));
    setSavingId(key);
    try {
      await api.put(`/components/${compId}/types/${typeId}/designs/${designId}/stock`, { stock_qty: value });
      setComponents((prev) =>
        prev.map((c) =>
          c.id === compId
            ? { ...c, types: c.types.map((t) => (t.id === typeId ? { ...t, design_stock: { ...(t.design_stock || {}), [designId]: value } } : t)) }
            : c
        )
      );
      setQtys((prev) => ({ ...prev, [key]: String(value) }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setSavingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-slate-400" data-testid="component-inventory-loading">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  const printInventory = (nonZeroOnly = false) => {
    const gs = invComponents
      .filter((c) => (c.types || []).length)
      .map((c) => ({
        name: c.name,
        rows: (c.types || []).flatMap((t) => {
          const im = modeOf(c);
          const rowFor = (key, name, designId) => {
            const qty = Math.max(0, parseFloat(qtys[key]) || 0);
            const reserved = Math.round(Math.max(0, reservations[key]?.reserved || 0) * 100) / 100;
            const avail = Math.round((qty - reserved) * 100) / 100;
            let stockText = num(qty);
            if (im === "film" || im === "label") {
              const summ = (t.roll_summary || {})[designId || "_"] || {};
              const unit = im === "label" ? "labels" : (summ.unit || t.roll_weight_unit || "lbs");
              stockText = `${num(qty)} ${unit} · ${summ.physical_rolls || 0} roll${(summ.physical_rolls || 0) === 1 ? "" : "s"}${summ.partial_count ? ` · ${summ.partial_count} partial` : ""}`;
            } else if (qty > 0 && t.packaging_mode !== "weight" && c.show_pallet_breakdown !== false) {
              const upcN = Number(t.units_per_case) || 0;
              const uppN = Number(t.units_per_pallet) || (upcN && Number(t.cases_per_pallet) ? upcN * Number(t.cases_per_pallet) : 0);
              if (uppN || upcN) {
                const pallets = uppN ? Math.floor(qty / uppN) : 0;
                const afterP = qty - pallets * uppN;
                const cases = upcN ? Math.floor(afterP / upcN) : 0;
                const units = Math.round((afterP - cases * upcN) * 100) / 100;
                const parts = [];
                if (pallets > 0) parts.push(`${pallets.toLocaleString()} Pallet${pallets === 1 ? "" : "s"}`);
                if (cases > 0) parts.push(`${cases.toLocaleString()} Case${cases === 1 ? "" : "s"}`);
                if (units > 0) parts.push(`${units.toLocaleString()} Unit${units === 1 ? "" : "s"}`);
                if (parts.length) stockText = `${num(qty)} (${parts.join(" · ")})`;
              }
            }
            const covd = toCoverage({ typeId: key, compId: c.id, avail: Math.max(0, avail) });
            const truckloads = covd.cov != null ? `${covd.cov.toLocaleString()}${covd.covAvg ? " (avg)" : ""}` : "—";
            return { name, stockText, reserved, avail, qty, truckloads };
          };
          const ds = t.designs || [];
          if (ds.length) return ds.map((d) => rowFor(`${t.id}::${d.id}`, `${t.name} · ${d.name}`, d.id));
          return [rowFor(t.id, t.name, "")];
        }).filter((r) => !nonZeroOnly || r.qty > 0),
      }))
      .filter((g) => g.rows.length > 0);
    printInventoryReport(gs, nonZeroOnly ? "Component Inventory — In-Stock Items" : "Component Inventory");
  };

  const modeToggle = (
    <div className="inline-flex rounded-sm border border-slate-200 bg-slate-50 p-1" data-testid="inventory-view-toggle">
      <button onClick={() => setMode("graph")} className={`rounded-sm px-4 py-1.5 text-sm font-semibold transition-colors ${mode === "graph" ? "bg-white text-[#002FA7] shadow-sm" : "text-slate-500 hover:text-slate-800"}`} data-testid="view-graph">Graph View</button>
      <button onClick={() => setMode("shelf")} className={`rounded-sm px-4 py-1.5 text-sm font-semibold transition-colors ${mode === "shelf" ? "bg-white text-[#002FA7] shadow-sm" : "text-slate-500 hover:text-slate-800"}`} data-testid="view-shelf">Shelf View</button>
    </div>
  );

  if (mode === "shelf") {
    return (
      <div className="space-y-6" data-testid="component-inventory">
        <div className="flex justify-end">{modeToggle}</div>
        <ShelfView />
      </div>
    );
  }

  if (components.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center border border-dashed border-slate-300 px-6 py-24 text-center">
        <Package className="h-10 w-10 text-slate-300" strokeWidth={1.5} />
        <h2 className="mt-4 font-heading text-xl font-bold tracking-tight text-slate-900">No components yet</h2>
        <p className="mt-2 max-w-md text-sm text-slate-500">Add components first, then track their stock levels here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="component-inventory">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2" data-testid="inventory-view-controls">
          <div className="inline-flex rounded-sm border border-slate-200 bg-slate-50 p-1">
            <button onClick={() => setViewCalc("qty")} className={`rounded-sm px-3 py-1 text-xs font-semibold transition-colors ${!coverage ? "bg-white text-[#002FA7] shadow-sm" : "text-slate-500 hover:text-slate-800"}`} data-testid="view-calc-qty">Inventory Quantity</button>
            <button onClick={() => setViewCalc("coverage")} className={`rounded-sm px-3 py-1 text-xs font-semibold transition-colors ${coverage ? "bg-white text-[#002FA7] shadow-sm" : "text-slate-500 hover:text-slate-800"}`} data-testid="view-calc-coverage">Truckload Coverage</button>
          </div>
          <div className="inline-flex rounded-sm border border-slate-200 bg-slate-50 p-1" data-testid="inventory-material-filter">
            {[["all", "All"], ["PET", "PET"], ["ALUM", "ALUM"]].map(([v, lbl]) => (
              <button key={v} onClick={() => setMaterialFilter(v)}
                className={`rounded-sm px-3 py-1 text-xs font-semibold transition-colors ${materialFilter === v ? "bg-white text-[#002FA7] shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
                data-testid={`inventory-material-${v.toLowerCase()}`}>
                {lbl}
              </button>
            ))}
          </div>
          {coverage && (
            <>
              <Select value={covProductId || "__avg"} onValueChange={(v) => { setCovProductId(v === "__avg" ? "" : v); setCovCfgId(""); }}>
                <SelectTrigger className="h-8 w-56 rounded-sm text-xs" data-testid="coverage-product-select"><span>{covProduct?.name || "Bottle Size: All / Average"}</span></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__avg">All / Average</SelectItem>
                  {products.map((pp) => (<SelectItem key={pp.id} value={pp.id}>{pp.name}</SelectItem>))}
                </SelectContent>
              </Select>
              <Select value={covCfgId || "__avg"} onValueChange={(v) => setCovCfgId(v === "__avg" ? "" : v)} disabled={!covProductId}>
                <SelectTrigger className="h-8 w-44 rounded-sm text-xs" data-testid="coverage-casepack-select"><span>{covCfg ? (covCfg.label || `${covCfg.bottles_per_case} Pack`) : "Case Pack: All / Average"}</span></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__avg">All / Average</SelectItem>
                  {covCfgs.map((c) => (<SelectItem key={c.id} value={c.id}>{c.label || `${c.bottles_per_case} Pack`}</SelectItem>))}
                </SelectContent>
              </Select>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={exportExcel}
            className="inline-flex items-center gap-1.5 rounded-sm border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-600 transition-colors hover:text-[#002FA7]"
            title="Download this inventory as an Excel file" data-testid="export-excel-button">
            <Download className="h-4 w-4" /> Excel
          </button>
          <button type="button" onClick={printProdReport}
            className="inline-flex items-center gap-1.5 rounded-sm border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-600 transition-colors hover:text-[#002FA7]"
            title="Printable report: Preforms, Caps, Labels & Trays inventory vs production reservations" data-testid="prod-report-button">
            <Factory className="h-4 w-4" /> Inventory vs Production
          </button>
          <button type="button" onClick={exportIvpExcel}
            className="inline-flex items-center gap-1.5 rounded-sm border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-600 transition-colors hover:text-[#002FA7]"
            title="Download the Inventory vs Production report as an Excel file" data-testid="ivp-excel-button">
            <Download className="h-4 w-4" /> IvP Excel
          </button>
          <button type="button" onClick={toggleCharts}
            className={`inline-flex items-center gap-1.5 rounded-sm border px-3 py-2 text-sm font-semibold transition-colors ${showCharts ? "border-[#002FA7]/30 bg-[#002FA7]/5 text-[#002FA7]" : "border-slate-200 bg-white text-slate-600 hover:text-[#002FA7]"}`}
            data-testid="toggle-charts-button">
            <BarChart3 className="h-4 w-4" /> {showCharts ? "Hide Graphs" : "Show Graphs"}
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className="inline-flex items-center gap-1.5 rounded-sm border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-600 transition-colors hover:text-[#002FA7]" data-testid="print-inventory-button">
                <Printer className="h-4 w-4" /> Print
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-sm">
              <DropdownMenuItem onClick={() => printInventory(false)} data-testid="print-inventory-all">All inventory</DropdownMenuItem>
              <DropdownMenuItem onClick={() => printInventory(true)} data-testid="print-inventory-nonzero">In-stock only (non-zero)</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {modeToggle}
        </div>
      </div>
      {showCharts && legend.length > 0 && (
        <div className="flex flex-wrap gap-x-5 gap-y-2" data-testid="component-inventory-legend">
          {legend.map((c) => (
            <div key={c.id} className="flex items-center gap-2 text-xs text-slate-600">
              <span className="inline-block h-3 w-3 rounded-sm" style={{ backgroundColor: colorByComp[c.id] }} />
              {c.name}
            </div>
          ))}
        </div>
      )}

      {showCharts && (
        <GroupedVariantChart groups={mainGroups} yMax={chartYMax(mainGroups)} title={coverage ? (covSpecific ? "Components — Truckloads Supported" : "Components — Truckloads Supported (Average of All Configurations)") : "Components — Units in Stock by Variant"} peak={chartPeak(mainGroups)} testId="component-inventory-chart" />
      )}

      {showCharts && alumGroups.length > 0 && (
        <GroupedVariantChart groups={alumGroups} yMax={chartYMax(alumGroups)} title={coverage ? (covSpecific ? "ALUM — Truckloads Supported" : "ALUM — Truckloads Supported (Average of All Configurations)") : "ALUM — Units in Stock by Variant"} peak={chartPeak(alumGroups)} testId="alum-inventory-chart"
          actions={
            <div className="inline-flex rounded-sm border border-slate-200 bg-slate-50 p-0.5" data-testid="alum-sort-toggle">
              {[["qty", "High → Low"], ["alpha", "A–Z"]].map(([k, lbl]) => (
                <button key={k} type="button" onClick={() => setAlumSort(k)}
                  className={`rounded-sm px-2.5 py-1 text-[11px] font-semibold transition-colors ${alumSort === k ? "bg-white text-[#002FA7] shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
                  data-testid={`alum-sort-${k}`}>{lbl}</button>
              ))}
            </div>
          } />
      )}

      <div className="max-w-7xl overflow-x-auto border border-slate-200" data-testid="component-inventory-table">
        <div className="w-full">
        <div className={`grid ${GRID} border-b border-slate-200 bg-slate-100 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-slate-700`}>
          <span>Variant</span>
          <span className="pr-10 text-right">On Order</span>
          <span className="text-right">Units in Stock</span>
          <span className="text-right">At Co-Packers</span>
          <span className="text-right">Reserved for Sales</span>
          <span className="text-right">Reserved for Projections</span>
          <span className="text-right">Available After Sales</span>
          <span className="text-right">Available After Projections</span>
          <span className="text-right">Available After Purchases</span>
          <span className="text-right">Consumed (30d)</span>
        </div>
        {invComponents.map((c) => (
          <div key={c.id} data-testid={`inventory-group-${c.id}`}>
            <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-4 py-2">
              {!isLabelComp(c.name) && (
                <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: colorByComp[c.id] || "#94a3b8" }} />
              )}
              <span className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">{c.name}</span>
            </div>
            {(c.types || []).length === 0 ? (
              <div className="px-4 py-2.5 text-sm text-slate-400">No variants yet.</div>
            ) : (
              (c.types || []).map((t) => {
                const designs = t.designs || [];
                if (designs.length > 0) {
                  return (
                    <div key={t.id} data-testid={`inventory-type-${t.id}`}>
                      <div className="border-b border-slate-100 bg-white px-4 py-1.5 pl-5 text-xs font-semibold text-slate-600">{t.name}</div>
                      {designs.map((d) => {
                        const key = `${t.id}::${d.id}`;
                        return (
                          <div key={key} className={`grid ${GRID} items-center border-b border-slate-100 px-4 py-2.5 last:border-b-0`} data-testid={`inventory-row-${key}`}>
                            <span className="pl-9 text-sm font-medium text-slate-800">{d.name}</span>
                            <OnOrderCell rkey={key} onOrder={onOrder} onOpen={() => { setOoKey(key); setOoName(`${t.name} · ${d.name}`); }} />
                            {stockCol(c, t, key, d.id)}
                            <CoPackerCell rows={cpRows(c.id, t.id, d.id)} testId={`copacker-col-${key}`} covFor={(v) => toCoverage({ typeId: key, compId: c.id, avail: v })} coPackers={coPackers} onAdjust={(cpId, delta) => adjustCp(c.id, t.id, d.id, cpId, delta)} />
                            <ReservedAvailCells rkey={key} stock={stockFor(key)} reservations={reservations} onOrder={onOrder} onOpen={(mode) => openResv(key, `${t.name} · ${d.name}`, mode)}
                              covFor={(v) => toCoverage({ typeId: key, compId: c.id, avail: Math.max(0, v) })} />
                            <ConsumedCell rkey={key} consumption={consumption} onOpen={() => openCons(key, `${t.name} · ${d.name}`)} />
                          </div>
                        );
                      })}
                    </div>
                  );
                }
                return (
                  <div key={t.id} className={`grid ${GRID} items-center border-b border-slate-100 px-4 py-2.5 last:border-b-0`} data-testid={`inventory-row-${t.id}`}>
                    <span className="pl-5 text-sm font-medium text-slate-800">{t.name}</span>
                    <OnOrderCell rkey={t.id} onOrder={onOrder} onOpen={() => { setOoKey(t.id); setOoName(`${c.name} · ${t.name}`); }} />
                    {stockCol(c, t, t.id)}
                    <CoPackerCell rows={cpRows(c.id, t.id, "")} testId={`copacker-col-${t.id}`} covFor={(v) => toCoverage({ typeId: t.id, compId: c.id, avail: v })} />
                    <ReservedAvailCells rkey={t.id} stock={stockFor(t.id)} reservations={reservations} onOrder={onOrder} onOpen={(mode) => openResv(t.id, `${c.name} · ${t.name}`, mode)}
                      covFor={(v) => toCoverage({ typeId: t.id, compId: c.id, avail: Math.max(0, v) })} />
                    <ConsumedCell rkey={t.id} consumption={consumption} onOpen={() => openCons(t.id, `${c.name} · ${t.name}`)} />
                  </div>
                );
              })
            )}
          </div>
        ))}
        </div>
      </div>

      <ReservationsDialog resvKey={resvKey} resvName={resvName} reservations={reservations} mode={resvMode}
        stock={resvKey ? stockFor(resvKey) : 0} onOrderQty={resvKey ? (onOrder[resvKey]?.on_order || 0) : 0}
        products={products} components={components}
        onChanged={() => api.get("/components/reservations").then(({ data }) => setReservations(data?.by_key || {})).catch(() => {})}
        onClose={() => setResvKey(null)} />
      <ConsumptionDialog consKey={consKey} consName={consName} consumption={consumption} onClose={() => setConsKey(null)} />
      <OnOrderDialog ooKey={ooKey} ooName={ooName} onOrder={onOrder} onClose={() => setOoKey(null)} />

      {rollDlg.open && (() => {
        const freshC = components.find((x) => x.id === rollDlg.component?.id) || rollDlg.component;
        const freshT = (freshC?.types || []).find((x) => x.id === rollDlg.type?.id) || rollDlg.type;
        return (
          <RollStockDialog
            open={rollDlg.open}
            onOpenChange={(o) => setRollDlg((p) => ({ ...p, open: o }))}
            component={freshC}
            type={freshT}
            designId={rollDlg.designId}
            initialView={rollDlg.view || "add"}
            onDone={load}
          />
        );
      })()}
    </div>
  );
}
